import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Card,
    Button,
    Input,
    Table,
    Spinner,
    Flex,
    Alert,
    SimpleGrid,
    Badge,
    IconButton,
    Dialog,
    Portal,
    CloseButton,
    InputGroup,
} from '@chakra-ui/react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaChevronDown, FaChevronRight, FaUsers, FaUserTie, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';

const API_URL = config.BACKEND_URL;

const CityPorts = () => {
    const { hasRole } = useAuth();
    const [cityPorts, setCityPorts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('');

    // Form state for new city port
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCityPort, setNewCityPort] = useState({ name: '' });
    const [newPics, setNewPics] = useState([{ pic_name: '', pic_email: '', pic_phone: '' }]);

    const [editingId, setEditingId] = useState(null);
    const [editingData, setEditingData] = useState({});

    // Expanded rows for viewing agents and PICs
    const [expandedRows, setExpandedRows] = useState({});
    const [agentsData, setAgentsData] = useState({});
    const [loadingAgents, setLoadingAgents] = useState({});

    // PIC Dialog state
    const [picDialogOpen, setPicDialogOpen] = useState(false);
    const [selectedCityPort, setSelectedCityPort] = useState(null);
    const [newPic, setNewPic] = useState({ pic_name: '', pic_email: '', pic_phone: '' });
    const [addingPic, setAddingPic] = useState(false);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    const loadCityPorts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/city-ports/with-agents`, {
                withCredentials: true
            });
            setCityPorts(response.data);
        } catch (error) {
            console.error('Error loading city ports:', error);
            try {
                const fallbackResponse = await axios.get(`${API_URL}/api/city-ports`, {
                    withCredentials: true
                });
                setCityPorts(fallbackResponse.data.map(cp => ({ ...cp, agent_count: 0 })));
            } catch (fallbackError) {
                setError('Failed to load city ports');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCityPorts();
    }, [loadCityPorts]);

    const showMessage = (msg, type = 'success') => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => setMessage(null), 3000);
    };

    // Filter city ports
    const filteredCityPorts = cityPorts.filter(cp => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const nameMatch = cp.name.toLowerCase().includes(term);
        const picMatch = cp.pics && cp.pics.some(pic => pic.pic_name.toLowerCase().includes(term));
        const legacyPicMatch = cp.pic_name && cp.pic_name.toLowerCase().includes(term);
        return nameMatch || picMatch || legacyPicMatch;
    });

    const toggleExpandRow = async (cityPortId) => {
        const isExpanded = expandedRows[cityPortId];

        if (!isExpanded && !agentsData[cityPortId]) {
            setLoadingAgents(prev => ({ ...prev, [cityPortId]: true }));
            try {
                const response = await axios.get(`${API_URL}/api/city-ports/by-city-port/${cityPortId}/agents`, {
                    withCredentials: true
                });
                setAgentsData(prev => ({ ...prev, [cityPortId]: response.data }));
            } catch (error) {
                console.error('Error loading agents:', error);
                setAgentsData(prev => ({ ...prev, [cityPortId]: [] }));
            } finally {
                setLoadingAgents(prev => ({ ...prev, [cityPortId]: false }));
            }
        }

        setExpandedRows(prev => ({ ...prev, [cityPortId]: !isExpanded }));
    };



    // Add new PIC row in create form
    const addNewPicRow = () => {
        setNewPics([...newPics, { pic_name: '', pic_email: '', pic_phone: '' }]);
    };

    const removePicRow = (index) => {
        if (newPics.length > 1) {
            setNewPics(newPics.filter((_, i) => i !== index));
        }
    };

    const updateNewPic = (index, field, value) => {
        const updated = [...newPics];
        updated[index][field] = value;
        setNewPics(updated);
    };

    const handleAddCityPort = async (e) => {
        e.preventDefault();

        if (!newCityPort.name.trim()) {
            showMessage('City port name is required', 'error');
            return;
        }

        try {
            const picsToSend = newPics
                .filter(p => p.pic_name.trim())
                .map(p => ({ name: p.pic_name, email: p.pic_email, phone: p.pic_phone }));

            const response = await axios.post(`${API_URL}/api/city-ports`, {
                name: newCityPort.name,
                pics: picsToSend
            }, { withCredentials: true });

            showMessage(response.data.message, 'success');
            setNewCityPort({ name: '' });
            setNewPics([{ pic_name: '', pic_email: '', pic_phone: '' }]);
            setShowAddForm(false);
            loadCityPorts();
        } catch (error) {
            showMessage(error.response?.data?.error || 'Failed to add city port', 'error');
        }
    };

    const handleEdit = (cityPort) => {
        setEditingId(cityPort.id);
        setEditingData({ name: cityPort.name });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingData({});
    };

    const handleSaveEdit = async () => {
        if (!editingData.name.trim()) {
            showMessage('City port name is required', 'error');
            return;
        }

        try {
            const response = await axios.put(`${API_URL}/api/city-ports/${editingId}`, editingData, {
                withCredentials: true
            });
            showMessage(response.data.message, 'success');
            setEditingId(null);
            setEditingData({});
            loadCityPorts();
        } catch (error) {
            showMessage(error.response?.data?.error || 'Failed to update city port', 'error');
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
            return;
        }

        try {
            const response = await axios.delete(`${API_URL}/api/city-ports/${id}`, {
                withCredentials: true
            });
            showMessage(response.data.message, 'success');
            loadCityPorts();
        } catch (error) {
            showMessage(error.response?.data?.error || 'Failed to delete city port', 'error');
        }
    };

    // PIC Management
    const openPicDialog = (cityPort) => {
        setSelectedCityPort(cityPort);
        setNewPic({ pic_name: '', pic_email: '', pic_phone: '' });
        setPicDialogOpen(true);
    };

    const handleAddPic = async () => {
        if (!newPic.pic_name.trim()) {
            showMessage('PIC name is required', 'error');
            return;
        }

        setAddingPic(true);
        try {
            await axios.post(`${API_URL}/api/city-ports/${selectedCityPort.id}/pics`, newPic, {
                withCredentials: true
            });
            showMessage('PIC added successfully', 'success');
            setNewPic({ pic_name: '', pic_email: '', pic_phone: '' });
            loadCityPorts();
        } catch (error) {
            showMessage(error.response?.data?.error || 'Failed to add PIC', 'error');
        } finally {
            setAddingPic(false);
        }
    };

    const handleDeletePic = async (picId) => {
        if (!window.confirm('Are you sure you want to delete this PIC?')) return;

        try {
            await axios.delete(`${API_URL}/api/city-ports/pics/${picId}`, {
                withCredentials: true
            });
            showMessage('PIC deleted successfully', 'success');
            loadCityPorts();
        } catch (error) {
            showMessage(error.response?.data?.error || 'Failed to delete PIC', 'error');
        }
    };

    if (loading) {
        return (
            <Box minH="100vh" bg="gray.50" py={8}>
                <Container display="flex" justifyContent="center" alignItems="center" minH="50vh">
                    <Spinner size="xl" color="purple.500" />
                </Container>
            </Box>
        );
    }

    const canEdit = hasRole(['Editor', 'Admin']);
    const canDelete = hasRole(['Admin']);

    // Find selected city port with updated pics
    const currentCityPort = selectedCityPort ? cityPorts.find(cp => cp.id === selectedCityPort.id) || selectedCityPort : null;

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="container.xl">
                <VStack spacing={6} align="stretch">
                    {/* Header */}
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Heading size="lg" color="gray.800">City Ports Management</Heading>
                            <Text color="gray.600">Manage city ports, PICs, and view assigned agents</Text>
                        </Box>
                        {canEdit && (
                            <Button
                                colorPalette="purple"
                                variant="solid"
                                onClick={() => setShowAddForm(!showAddForm)}
                            >
                                <FaPlus style={{ marginRight: '8px' }} />
                                Add City Port
                            </Button>
                        )}
                    </Flex>

                    {/* Messages */}
                    {error && (
                        <Alert.Root status="error">
                            <Alert.Content>{error}</Alert.Content>
                        </Alert.Root>
                    )}
                    {message && (
                        <Alert.Root status={messageType === 'error' ? 'error' : 'success'}>
                            <Alert.Content>{message}</Alert.Content>
                        </Alert.Root>
                    )}

                    {/* Add Form */}
                    {showAddForm && canEdit && (
                        <Card.Root shadow="lg" borderRadius="xl">
                            <Card.Header>
                                <Heading size="md">Add New City Port</Heading>
                            </Card.Header>
                            <Card.Body>
                                <form onSubmit={handleAddCityPort}>
                                    <Box mb={4}>
                                        <Text mb={1} fontWeight="medium">City Port Name *</Text>
                                        <Input
                                            placeholder="Enter city port name"
                                            value={newCityPort.name}
                                            onChange={(e) => setNewCityPort({ ...newCityPort, name: e.target.value })}
                                        />
                                    </Box>

                                    <Box mb={4}>
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontWeight="medium">PICs (Person In Charge)</Text>
                                            <Button size="xs" variant="ghost" colorPalette="purple" onClick={addNewPicRow}>
                                                + Add PIC
                                            </Button>
                                        </Flex>
                                        <VStack spacing={2} align="stretch">
                                            {newPics.map((pic, index) => (
                                                <Flex key={index} gap={2} align="center">
                                                    <Input
                                                        size="sm"
                                                        placeholder="PIC Name"
                                                        value={pic.pic_name}
                                                        onChange={(e) => updateNewPic(index, 'pic_name', e.target.value)}
                                                    />
                                                    <Input
                                                        size="sm"
                                                        placeholder="Email"
                                                        value={pic.pic_email}
                                                        onChange={(e) => updateNewPic(index, 'pic_email', e.target.value)}
                                                    />
                                                    <Input
                                                        size="sm"
                                                        placeholder="Phone"
                                                        value={pic.pic_phone}
                                                        onChange={(e) => updateNewPic(index, 'pic_phone', e.target.value)}
                                                    />
                                                    {newPics.length > 1 && (
                                                        <IconButton
                                                            aria-label="Remove PIC"
                                                            size="sm"
                                                            variant="ghost"
                                                            colorPalette="red"
                                                            onClick={() => removePicRow(index)}
                                                        >
                                                            <FaTimes />
                                                        </IconButton>
                                                    )}
                                                </Flex>
                                            ))}
                                        </VStack>
                                    </Box>

                                    <HStack justify="flex-end">
                                        <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
                                        <Button type="submit" colorPalette="purple" variant="solid">Add City Port</Button>
                                    </HStack>
                                </form>
                            </Card.Body>
                        </Card.Root>
                    )}

                    {/* City Ports Table */}
                    <Card.Root shadow="lg" borderRadius="xl">
                        <Card.Header py={4} borderBottomWidth="1px">
                            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                                <HStack>
                                    <Heading size="md">City Ports</Heading>
                                    <Badge colorPalette="purple" size="lg">{filteredCityPorts.length} total</Badge>
                                </HStack>
                                <InputGroup
                                    maxW="300px"
                                    startElement={<FaSearch color="gray" />}
                                    endElement={
                                        searchTerm && (
                                            <IconButton
                                                size="xs"
                                                variant="ghost"
                                                colorPalette="gray"
                                                onClick={() => setSearchTerm('')}
                                                aria-label="Clear search"
                                                rounded="full"
                                            >
                                                <FaTimes />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <Input
                                        placeholder="Search by name or PIC..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        borderRadius="md"
                                    />
                                </InputGroup>
                            </Flex>
                        </Card.Header>
                        <Card.Body p={0}>
                            {cityPorts.length === 0 ? (
                                <Box p={8} textAlign="center">
                                    <Text color="gray.500">No city ports found. Add your first city port!</Text>
                                </Box>
                            ) : (
                                <Box overflowX="auto">
                                    <Table.Root size="sm">
                                        <Table.Header>
                                            <Table.Row bg="gray.50">
                                                <Table.ColumnHeader w="40px"></Table.ColumnHeader>
                                                <Table.ColumnHeader>Name</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign="center">PICs</Table.ColumnHeader>
                                                <Table.ColumnHeader textAlign="center">Agents</Table.ColumnHeader>
                                                {canEdit && <Table.ColumnHeader textAlign="center">Actions</Table.ColumnHeader>}
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {filteredCityPorts.map((cityPort) => (
                                                <React.Fragment key={cityPort.id}>
                                                    <Table.Row _hover={{ bg: 'gray.50' }}>
                                                        <Table.Cell>
                                                            {(parseInt(cityPort.agent_count) > 0 || (cityPort.pics && cityPort.pics.length > 0)) && (
                                                                <IconButton
                                                                    aria-label="Expand"
                                                                    size="xs"
                                                                    variant="ghost"
                                                                    onClick={() => toggleExpandRow(cityPort.id)}
                                                                >
                                                                    {expandedRows[cityPort.id] ? <FaChevronDown /> : <FaChevronRight />}
                                                                </IconButton>
                                                            )}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {editingId === cityPort.id ? (
                                                                <Input
                                                                    size="sm"
                                                                    value={editingData.name}
                                                                    onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
                                                                />
                                                            ) : (
                                                                <Text fontWeight="medium">{cityPort.name}</Text>
                                                            )}
                                                        </Table.Cell>
                                                        <Table.Cell textAlign="center">
                                                            <Badge
                                                                colorPalette={cityPort.pics?.length > 0 ? 'green' : 'gray'}
                                                                variant={cityPort.pics?.length > 0 ? 'solid' : 'subtle'}
                                                                cursor={canEdit ? 'pointer' : 'default'}
                                                                onClick={canEdit ? () => openPicDialog(cityPort) : undefined}
                                                            >
                                                                <FaUserTie style={{ marginRight: '4px', display: 'inline' }} />
                                                                {cityPort.pics?.length || 0}
                                                            </Badge>
                                                        </Table.Cell>
                                                        <Table.Cell textAlign="center">
                                                            <Badge
                                                                colorPalette={parseInt(cityPort.agent_count) > 0 ? 'blue' : 'gray'}
                                                                variant={parseInt(cityPort.agent_count) > 0 ? 'solid' : 'subtle'}
                                                            >
                                                                <FaUsers style={{ marginRight: '4px', display: 'inline' }} />
                                                                {cityPort.agent_count || 0}
                                                            </Badge>
                                                        </Table.Cell>
                                                        {canEdit && (
                                                            <Table.Cell>
                                                                <HStack justify="center" gap={2}>
                                                                    {editingId === cityPort.id ? (
                                                                        <>
                                                                            <IconButton
                                                                                aria-label="Save"
                                                                                size="sm"
                                                                                colorPalette="green"
                                                                                variant="ghost"
                                                                                onClick={handleSaveEdit}
                                                                            >
                                                                                <FaSave />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                aria-label="Cancel"
                                                                                size="sm"
                                                                                colorPalette="gray"
                                                                                variant="ghost"
                                                                                onClick={handleCancelEdit}
                                                                            >
                                                                                <FaTimes />
                                                                            </IconButton>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <IconButton
                                                                                aria-label="Manage PICs"
                                                                                size="sm"
                                                                                colorPalette="green"
                                                                                variant="ghost"
                                                                                onClick={() => openPicDialog(cityPort)}
                                                                            >
                                                                                <FaUserTie />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                aria-label="Edit"
                                                                                size="sm"
                                                                                colorPalette="purple"
                                                                                variant="ghost"
                                                                                onClick={() => handleEdit(cityPort)}
                                                                            >
                                                                                <FaEdit />
                                                                            </IconButton>
                                                                            {canDelete && (
                                                                                <IconButton
                                                                                    aria-label="Delete"
                                                                                    size="sm"
                                                                                    colorPalette="red"
                                                                                    variant="ghost"
                                                                                    onClick={() => handleDelete(cityPort.id, cityPort.name)}
                                                                                >
                                                                                    <FaTrash />
                                                                                </IconButton>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </HStack>
                                                            </Table.Cell>
                                                        )}
                                                    </Table.Row>

                                                    {/* Expanded row showing PICs and Agents */}
                                                    {expandedRows[cityPort.id] && (
                                                        <Table.Row>
                                                            <Table.Cell colSpan={canEdit ? 5 : 4} bg="gray.50" p={4}>
                                                                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                                                    {/* PICs Section */}
                                                                    <Box>
                                                                        <Text fontWeight="bold" mb={2} color="green.600">
                                                                            <FaUserTie style={{ display: 'inline', marginRight: '8px' }} />
                                                                            PICs ({cityPort.pics?.length || 0})
                                                                        </Text>
                                                                        {cityPort.pics && cityPort.pics.length > 0 ? (
                                                                            <VStack align="stretch" spacing={2}>
                                                                                {cityPort.pics.map(pic => (
                                                                                    <Box
                                                                                        key={pic.id}
                                                                                        bg="white"
                                                                                        p={3}
                                                                                        borderRadius="md"
                                                                                        shadow="sm"
                                                                                        border="1px"
                                                                                        borderColor="gray.200"
                                                                                    >
                                                                                        <Flex justify="space-between" align="center">
                                                                                            <Box>
                                                                                                <Text fontWeight="medium">{pic.pic_name}</Text>
                                                                                                {pic.pic_email && (
                                                                                                    <Text fontSize="xs" color="gray.500">{pic.pic_email}</Text>
                                                                                                )}
                                                                                                {pic.pic_phone && (
                                                                                                    <Text fontSize="xs" color="gray.500">{pic.pic_phone}</Text>
                                                                                                )}
                                                                                            </Box>
                                                                                            {canDelete && (
                                                                                                <IconButton
                                                                                                    aria-label="Delete PIC"
                                                                                                    size="xs"
                                                                                                    variant="ghost"
                                                                                                    colorPalette="red"
                                                                                                    onClick={() => handleDeletePic(pic.id)}
                                                                                                >
                                                                                                    <FaTrash />
                                                                                                </IconButton>
                                                                                            )}
                                                                                        </Flex>
                                                                                    </Box>
                                                                                ))}
                                                                            </VStack>
                                                                        ) : (
                                                                            <Text color="gray.500" fontSize="sm">No PICs assigned</Text>
                                                                        )}
                                                                    </Box>

                                                                    {/* Agents Section */}
                                                                    <Box>
                                                                        <Text fontWeight="bold" mb={2} color="blue.600">
                                                                            <FaUsers style={{ display: 'inline', marginRight: '8px' }} />
                                                                            Agents ({agentsData[cityPort.id]?.length || 0})
                                                                        </Text>
                                                                        {loadingAgents[cityPort.id] ? (
                                                                            <Flex justify="center" py={4}>
                                                                                <Spinner size="sm" color="blue.500" />
                                                                            </Flex>
                                                                        ) : agentsData[cityPort.id]?.length > 0 ? (
                                                                            <VStack align="stretch" spacing={2}>
                                                                                {agentsData[cityPort.id].map(agent => (
                                                                                    <Box
                                                                                        key={agent.id}
                                                                                        bg="white"
                                                                                        p={3}
                                                                                        borderRadius="md"
                                                                                        shadow="sm"
                                                                                        border="1px"
                                                                                        borderColor="gray.200"
                                                                                    >
                                                                                        <Text fontWeight="medium">{agent.agent_name}</Text>
                                                                                        <HStack spacing={2} mt={1}>
                                                                                            <Badge colorPalette="purple" size="sm">{agent.country}</Badge>
                                                                                            <Badge
                                                                                                colorPalette={agent.status === 'APPROVED' ? 'green' : 'yellow'}
                                                                                                size="sm"
                                                                                            >
                                                                                                {agent.status}
                                                                                            </Badge>
                                                                                        </HStack>
                                                                                    </Box>
                                                                                ))}
                                                                            </VStack>
                                                                        ) : (
                                                                            <Text color="gray.500" fontSize="sm">No agents assigned</Text>
                                                                        )}
                                                                    </Box>
                                                                </SimpleGrid>
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </Table.Body>
                                    </Table.Root>
                                </Box>
                            )}
                        </Card.Body>
                    </Card.Root>
                </VStack>
            </Container>

            {/* PIC Management Dialog */}
            <Dialog.Root open={picDialogOpen} onOpenChange={(e) => setPicDialogOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content maxW="lg">
                            <Dialog.Header>
                                <Dialog.Title>
                                    Manage PICs - {currentCityPort?.name}
                                </Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                {/* List existing PICs */}
                                <Box mb={4}>
                                    <Text fontWeight="medium" mb={2}>Current PICs</Text>
                                    {currentCityPort?.pics?.length > 0 ? (
                                        <VStack align="stretch" spacing={2}>
                                            {currentCityPort.pics.map(pic => (
                                                <Flex
                                                    key={pic.id}
                                                    justify="space-between"
                                                    align="center"
                                                    p={3}
                                                    bg="gray.50"
                                                    borderRadius="md"
                                                >
                                                    <Box>
                                                        <Text fontWeight="medium">{pic.pic_name}</Text>
                                                        <Text fontSize="sm" color="gray.500">
                                                            {pic.pic_email || '-'} | {pic.pic_phone || '-'}
                                                        </Text>
                                                    </Box>
                                                    {canDelete && (
                                                        <IconButton
                                                            aria-label="Delete"
                                                            size="sm"
                                                            variant="ghost"
                                                            colorPalette="red"
                                                            onClick={() => handleDeletePic(pic.id)}
                                                        >
                                                            <FaTrash />
                                                        </IconButton>
                                                    )}
                                                </Flex>
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Text color="gray.500">No PICs assigned yet.</Text>
                                    )}
                                </Box>

                                {/* Add new PIC */}
                                {canEdit && (
                                    <Box bg="green.50" p={4} borderRadius="md">
                                        <Text fontWeight="medium" mb={2}>Add New PIC</Text>
                                        <SimpleGrid columns={3} gap={2} mb={2}>
                                            <Input
                                                size="sm"
                                                placeholder="Name *"
                                                value={newPic.pic_name}
                                                onChange={(e) => setNewPic({ ...newPic, pic_name: e.target.value })}
                                                bg="white"
                                            />
                                            <Input
                                                size="sm"
                                                placeholder="Email"
                                                value={newPic.pic_email}
                                                onChange={(e) => setNewPic({ ...newPic, pic_email: e.target.value })}
                                                bg="white"
                                            />
                                            <Input
                                                size="sm"
                                                placeholder="Phone"
                                                value={newPic.pic_phone}
                                                onChange={(e) => setNewPic({ ...newPic, pic_phone: e.target.value })}
                                                bg="white"
                                            />
                                        </SimpleGrid>
                                        <Button
                                            size="sm"
                                            colorPalette="green"
                                            onClick={handleAddPic}
                                            loading={addingPic}
                                            disabled={!newPic.pic_name.trim()}
                                        >
                                            <FaPlus style={{ marginRight: '4px' }} />
                                            Add PIC
                                        </Button>
                                    </Box>
                                )}
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button variant="outline" onClick={() => setPicDialogOpen(false)}>
                                    Close
                                </Button>
                            </Dialog.Footer>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </Box>
    );
};

export default CityPorts;
